import { Ref } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  postCreateCompanySpecificProduct,
  useGetAllCompanies,
  useGetCompanySpecificProducts,
} from "@/services/Company";
import { CreateCompanySpecificProductDto } from "@/types";
import { ContractDetailsInput, ContractDetailsSchema } from "../validation/contract-details";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CreateContractWizard } from "../helpers/helpers";
import { useAuth } from "@/contexts/AuthContext";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { ComboboxItem, Divider, Grid, OptionsFilter } from "@mantine/core";
import Select, { transformDataToSelectOption } from "@/components/atoms/Select";
import axios from "axios";
import RadioButtonGroup from "@/components/molecules/RadioButtonGroup";
import SelectProduct from "@/components/molecules/SelectInputs/SelectProduct";
import DynamicProductForm from "../../DynamicProductForm";

interface Props {
  onValidationSuccess: (data: any) => void;
  onChangeWizardData: (changed: any) => void;
  wizardData: CreateContractWizard;
  ref?: Ref<HTMLFormElement>;
}

function ContractDetails({ onValidationSuccess, onChangeWizardData, wizardData, ref }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { currentCompany } = useAuth();

  const { data: companiesTradingPartners } = useGetAllCompanies();
  const { data: companySpecificProducts, isFetching: isFetchingCompanySpecificProducts } =
    useGetCompanySpecificProducts(currentCompany?.companyId as number, false, {
      enabled: !!currentCompany,
    });

  const productTypeOptions = [
    { value: "new", label: t("contracts:newProduct") },
    { value: "saved", label: t("contracts:existingProduct") },
  ];

  const { mutateAsync: mutateCreateProductNew, isPending: isMutating } = useMutation({
    mutationFn: async (data: CreateCompanySpecificProductDto) => {
      return await postCreateCompanySpecificProduct(data);
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["getCompanySpecificProduct", currentCompany?.companyId] });

      onChangeWizardData({
        companySpecificProductId: data.data,
        selectFromExisting: true,
        name: variables.companySpecificProductName,
      });

      setValue("selectFromExisting", true);
      setValue("companySpecificProductId", data.data);

      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:settings.productCreated"))
      );
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          notifications.show(
            ErrorNotificationObj(t("notifications:generic.error"), t("validation:duplicateProductName"))
          );
        } else {
          notifications.show(
            ErrorNotificationObj(t("notifications:generic.error"), t("notifications:settings.productCreatedError"))
          );
        }
      }
    },
  });

  const existingProductDefaults = {
    selectFromExisting: true as const,
    tradingPartner: wizardData.tradingPartner || null,
    companySpecificProductId: wizardData.companySpecificProductId || 0,
  };

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ContractDetailsInput>({
    resolver: zodResolver(ContractDetailsSchema),
    mode: "onSubmit",
    values: {
      selectFromExisting: true,
      companySpecificProductId: wizardData.companySpecificProductId,
      tradingPartner: wizardData.tradingPartner,
    },
  });

  const newProductDefaults = {
    selectFromExisting: false as const,
    tradingPartner: watch("tradingPartner") || null,
    companyId: currentCompany?.companyId,
    companySpecificProductName: "",
    salesItemId: null,
    images: [],
    kgPerMeter: null,
    kgPerM3: null,
    kgPerStk: null,
    originalProductNumber: null,
  };

  const onSubmit = async (data: ContractDetailsInput) => {
    if (!watch("selectFromExisting")) {
      notifications.show(
        ErrorNotificationObj(t("notifications.generic.error"), t("validation:pleaseCreateProductFirst"))
      );
    } else {
      const product = companySpecificProducts?.find((product) => product.id === watch("companySpecificProductId"));
      const tradingPartner =
        companiesTradingPartners?.find((company) => company.id === data.tradingPartner)?.companyName ?? "";

      onChangeWizardData({
        ...data,
        kpPerM3: product?.kgPerM3 ?? null,
        kgPerMeter: product?.kgPerMeter ?? null,
        kgPerStk: product?.kgPerStk ?? null,
        name: `${tradingPartner} - ${product?.companySpecificProductName}`,
      });
      onValidationSuccess(data);
    }
  };

  const optionsFilter: OptionsFilter = ({ options, search }) => {
    const openDropdown = search.length >= 3;
    if (!openDropdown) return [];
    return (options as ComboboxItem[]).filter((option) => {
      return option.label.toLowerCase().includes(search.toLowerCase());
    });
  };

  return (
    <>
      <form ref={ref} onSubmit={handleSubmit(onSubmit)} data-testid="contract-details-form">
        <Grid>
          <Grid.Col span={12}>
            <Controller
              control={control}
              name="tradingPartner"
              render={({ field: { onChange, value } }) => (
                <Select
                  id="trading-partner-select"
                  label={t("contracts:tradingPartner")}
                  data-testid={"trading-partner-select"}
                  data={transformDataToSelectOption(
                    companiesTradingPartners
                      ? companiesTradingPartners.filter((company) => company.id !== currentCompany?.companyId)
                      : [],
                    "id",
                    "companyName"
                  )}
                  placeholder={t("contracts:searchForTradingPartner")}
                  filter={optionsFilter}
                  required
                  value={String(value)}
                  onChange={(value) => {
                    return onChange(value ? Number(value) : null);
                  }}
                  error={errors.tradingPartner ? t("validation:" + errors.tradingPartner?.message) : null}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Controller
              control={control}
              name="selectFromExisting"
              render={({ field: { onChange, value } }) => {
                return (
                  <RadioButtonGroup
                    id="create-contract-select-product-type"
                    title={t("contracts:commodity")}
                    name={"selectFromExisting"}
                    options={productTypeOptions}
                    defaultValue={value ? "saved" : "new"}
                    onValueChange={(val) => {
                      reset(
                        (val === "saved"
                          ? {
                              ...existingProductDefaults,
                              tradingPartner: watch("tradingPartner"),
                            }
                          : newProductDefaults) as ContractDetailsInput
                      );
                      onChange(val === "saved");
                    }}
                    inlineTitle={true}
                  />
                );
              }}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Divider />
          </Grid.Col>
          {watch("selectFromExisting") ? (
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Controller
                control={control}
                name="companySpecificProductId"
                render={({ field: { onChange, value }, fieldState }) => {
                  return (
                    <SelectProduct
                      id="existing-product-select"
                      data-testid="existing-product-select"
                      required
                      name="saved-products"
                      label={t("contracts:existingProduct")}
                      onChange={(value) => {
                        return onChange(value ? Number(value) : null);
                      }}
                      value={String(value)}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      valueKey={"id"}
                      labelKey={"companySpecificProductName"}
                      loading={isFetchingCompanySpecificProducts}
                    />
                  );
                }}
              />
            </Grid.Col>
          ) : null}
        </Grid>
      </form>
      {!watch("selectFromExisting") && (
        <DynamicProductForm onSubmitMutation={mutateCreateProductNew} isMutating={isMutating} />
      )}
    </>
  );
}

export default ContractDetails;
